'use client';

import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { ListRow } from '@/read';
import { ancestorSetsOf, type ParentEdge } from '@/domain/parent-path';
import {
  fromLiveRecord,
  liveRecordToListRow,
  recordToBoardItem,
  type LiveRecord,
  type RecordBoardItem,
} from '@/read/record-shape';

// Re-exported so the client half has ONE import point for the record shape; the
// definition stays in the read layer (src/read/record-shape.ts).
export type { LiveRecord, RecordBoardItem } from '@/read/record-shape';
import {
  effectiveShowClosed,
  criteriaMatch,
  passesShowClosed,
  type CriteriaOp,
  type Criterion,
} from '@/domain/search-criteria';
import { withJustTicked, type JustTickedRow } from '@/app/boards/completion';
import {
  adoptIdentity,
  loadRecords,
  loadSurfaces,
  persistRecords,
  persistSurface,
  type StoredRecord,
} from './live-idb';

/**
 * The per-surface push overlay (docs/SYNC.md §7.2, slice 2). The server props
 * are the base; pushes are an overlay applied on top; the overlay is cleared
 * when the props change (a navigation, or the `router.refresh()` fallback,
 * brought fresh server truth). This generalises `src/app/boards/completion.ts`:
 * its just-ticked set is one person's ticks over one surface; the overlay is
 * everyone's pushed rows over any surface.
 *
 * On top of the overlay sits the JUST-TICKED RULE (docs/SYNC.md §7.3): a row that
 * SOMEONE ELSE closes while it is on your screen open stays struck in place -
 * surviving the refresh that would hide it - until you leave the surface, so a
 * shopper still sees "this is done, I need not get it". That state is
 * `justTicked`, and unlike the overlay it is NOT cleared on a props change;
 * it is cleared only when the scope changes or the last reader leaves.
 *
 * What crosses the wire and what the device holds is the ITEM RECORD of §7.1
 * (`LiveRecord`, src/read/record-shape.ts); a surface renders its own pure
 * projection of it (`liveRecordToListRow`, `recordToBoardItem`).
 */

/** The overlay over one surface's server rows: rows to replace-or-append by id,
 *  and ids the server dropped. `upserts` and `removed` are kept disjoint by
 *  `mergePush`. */
export interface Overlay {
  upserts: ReadonlyMap<string, ListRow>;
  removed: ReadonlySet<string>;
}

/** A surface's live state: the push overlay, and the just-ticked rule's remotely
 *  closed rows kept struck in place. One immutable object, replaced (new
 *  reference) on every change, so it is a valid `useSyncExternalStore`
 *  snapshot. */
export interface SurfaceState {
  overlay: Overlay;
  justTicked: ReadonlyMap<string, JustTickedRow>;
}

/** Stable empty references, so `getSnapshot` stays cached (useSyncExternalStore
 *  loops if it returns a fresh empty object each call). */
const EMPTY_OVERLAY: Overlay = { upserts: new Map(), removed: new Set() };
const EMPTY_JUST_TICKED: ReadonlyMap<string, JustTickedRow> = new Map();
const EMPTY_STATE: SurfaceState = { overlay: EMPTY_OVERLAY, justTicked: EMPTY_JUST_TICKED };
const NO_ROWS: readonly ListRow[] = [];

/**
 * The server rows with the overlay applied: for an id the overlay upserts, the
 * FRESHER of the prop row and the upsert wins (greater `updatedAt`; on a tie the
 * upsert wins), so a push built from a read older than a later server render
 * never clobbers the newer prop row. An id the overlay carries but the base does
 * not is appended; a removed id is dropped. Untouched rows keep their order;
 * appended rows go at the end (the surface re-sorts anyway). Returns `propRows`
 * unchanged when the overlay is empty, so an un-pushed surface never re-sorts.
 * Pure.
 */
export function applyOverlay(propRows: ListRow[], overlay: Overlay): ListRow[] {
  if (overlay.upserts.size === 0 && overlay.removed.size === 0) return propRows;
  const out: ListRow[] = [];
  const seen = new Set<string>();
  for (const row of propRows) {
    if (overlay.removed.has(row.id)) continue;
    seen.add(row.id);
    const up = overlay.upserts.get(row.id);
    out.push(up && up.updatedAt.getTime() >= row.updatedAt.getTime() ? up : row);
  }
  for (const [id, row] of overlay.upserts) {
    if (!seen.has(id) && !overlay.removed.has(id)) out.push(row);
  }
  return out;
}

/**
 * Fold a push into the overlay, returning a NEW overlay. A pushed row upserts
 * only if its `updatedAt` is at or past the one already effective for its id (a
 * stale, out-of-order push is ignored) and clears any removed mark for it; a
 * dropped id is marked removed and drops any upsert. `upserts` and `removed`
 * stay disjoint. Pure.
 */
export function mergePush(overlay: Overlay, rows: ListRow[], dropped: string[]): Overlay {
  const upserts = new Map(overlay.upserts);
  const removed = new Set(overlay.removed);
  for (const row of rows) {
    const effective = upserts.get(row.id)?.updatedAt;
    if (effective && row.updatedAt.getTime() < effective.getTime()) continue;
    upserts.set(row.id, row);
    removed.delete(row.id);
  }
  for (const id of dropped) {
    removed.add(id);
    upserts.delete(id);
  }
  return { upserts, removed };
}

// --- the module-level store, one state per surface key ----------------------

interface SurfaceStore {
  state: SurfaceState;
  scope: string;
  /** What `useLiveRows` last returned - the just-ticked rule reads it to know an id
   *  is CURRENTLY on screen open before it keeps a remote closure. */
  lastRendered: ListRow[];
  /** Ids a tidy folded (`clearJustTicked`) that the rendered rows still carry
   *  OPEN: the props lag the tidy's refresh, so a catch-up landing in between
   *  would read the closure as new and seat the line again. Pruned by
   *  `setLastRendered` as soon as the rows stop carrying an id open. */
  folded: Set<string>;
  listeners: Set<() => void>;
}

const stores = new Map<string, SurfaceStore>();

function storeFor(key: string): SurfaceStore {
  let store = stores.get(key);
  if (!store) {
    store = {
      state: EMPTY_STATE,
      scope: '',
      lastRendered: [],
      folded: new Set(),
      listeners: new Set(),
    };
    stores.set(key, store);
  }
  return store;
}

/** Subscribe a reader to one surface's state; returns the unsubscribe. When the
 *  last reader leaves, the surface is dropped entirely - the just-ticked rule lives
 *  for the sitting, not beyond it (docs/SYNC.md §7.3). */
export function subscribe(key: string, listener: () => void): () => void {
  const store = storeFor(key);
  store.listeners.add(listener);
  return () => {
    store.listeners.delete(listener);
    if (store.listeners.size === 0) stores.delete(key);
  };
}

/** The current state for a surface (`EMPTY_STATE` for an unknown key). */
export function getSnapshot(key: string): SurfaceState {
  return stores.get(key)?.state ?? EMPTY_STATE;
}

/** Whether a surface has a live reader - a `useLiveRows` mounted and consuming
 *  it right now (a list rail on screen). The pushable query surfaces the server
 *  serves without one (a board, or a lens on its board/map/checklist rail) have
 *  no reader, so their push falls back to a refresh (see `pushToStore`). */
export function hasSubscriber(surfaceKey: string): boolean {
  const store = stores.get(surfaceKey);
  return store !== undefined && store.listeners.size > 0;
}

/** The rows the just-ticked rule stands back in for a surface, or an empty map. */
export function justTickedRows(surfaceKey: string): ReadonlyMap<string, JustTickedRow> {
  return stores.get(surfaceKey)?.state.justTicked ?? EMPTY_JUST_TICKED;
}

/**
 * Apply a push to a surface's state and wake its readers, returning whether the
 * surface had a live reader. A `true` means the store patched and nothing else
 * is needed; a `false` (no reader - a board or saved-list page that does not
 * consume the store this slice) tells the caller (`src/components/live.tsx`) to
 * refresh instead, so those surfaces keep updating. A reader-less push is
 * dropped, never buffered: the refresh reads everything again.
 *
 * Beyond the overlay, a pushed CLOSED row for an id currently rendered OPEN is
 * snapshotted into `justTicked` (the just-ticked rule, docs/SYNC.md §7.3); a
 * pushed OPEN row, or a drop, for an id in that map removes it (a reopen shows
 * through; a row that left the surface stops being stood back in).
 */
export function pushToStore(
  surfaceKey: string,
  rows: LiveRecord[],
  dropped: string[],
  cur?: string,
): boolean {
  // The DEVICE store takes every pushed record, reader or not (slice 3): the
  // records outlive the surface that happened to be open, and the cursor is
  // what a RE-connect hands back so the server replays the gap (§5.6).
  putRecords(rows);
  if (cur) setSurfaceCursor(surfaceKey, cur);
  const store = stores.get(surfaceKey);
  if (!store || store.listeners.size === 0) return false;
  const converted = rows.map(liveRecordToListRow);
  const overlay = mergePush(store.state.overlay, converted, dropped);
  let ticked: Map<string, JustTickedRow> | null = null;
  const editJustTicked = () => (ticked ??= new Map(store.state.justTicked));
  for (const row of converted) {
    if (row.closed) {
      // Already folded by a tidy: not a closure that happened in this sitting.
      if (store.folded.has(row.id)) continue;
      const index = store.lastRendered.findIndex((r) => r.id === row.id && !r.closed);
      if (index >= 0) editJustTicked().set(row.id, { row, index });
    } else if (store.state.justTicked.has(row.id)) {
      editJustTicked().delete(row.id);
    }
  }
  for (const id of dropped) {
    if ((ticked ?? store.state.justTicked).has(id)) editJustTicked().delete(id);
  }
  store.state = { overlay, justTicked: ticked ?? store.state.justTicked };
  for (const listener of store.listeners) listener();
  return true;
}

/**
 * Clear a surface's OVERLAY (never its just-ticked rule) and wake its readers.
 * `useLiveRows` calls this when its props change: the fresh server rows already
 * carry every push the overlay held, so that patch is spent - but a struck line
 * the just-ticked rule is holding must survive the refresh that hides it. A no-op
 * when the overlay is already empty. Exported for the store's unit test.
 */
export function resetSurface(surfaceKey: string): void {
  const store = stores.get(surfaceKey);
  if (!store || store.state.overlay === EMPTY_OVERLAY) return;
  store.state = { overlay: EMPTY_OVERLAY, justTicked: store.state.justTicked };
  for (const listener of store.listeners) listener();
}

/**
 * Record a surface's scope, clearing the just-ticked rule when it changes: a
 * different filter is a different list, and a strike-through minted under the
 * old one does not belong (docs/SYNC.md §7.3). Exported for the store's unit
 * test.
 */
export function setSurfaceScope(surfaceKey: string, scope: string): void {
  const store = stores.get(surfaceKey);
  if (!store || store.scope === scope) return;
  store.scope = scope;
  store.folded.clear();
  if (store.state.justTicked.size === 0) return;
  store.state = { overlay: store.state.overlay, justTicked: EMPTY_JUST_TICKED };
  for (const listener of store.listeners) listener();
}

/**
 * **The tidy's half in the store** (`Completion.tidy`, docs/CHECKLIST.md §3.1):
 * clear a surface's just-ticked rule (never its overlay) and wake its readers, so
 * the struck rows it held fold like any other closed row. It ends the sitting
 * on demand where the installed app has no reload to end it.
 *
 * `also` names the lines the surface itself kept (its own ticks), which the
 * store may not hold yet: every folded id is remembered until the rendered
 * rows stop carrying it open (`SurfaceStore.folded`), so the catch-up of a tick
 * that races the tidy cannot seat the line again. Exported for the store's
 * unit test.
 */
export function clearJustTicked(surfaceKey: string, also: Iterable<string> = []): void {
  const store = stores.get(surfaceKey);
  if (!store) return;
  for (const id of also) store.folded.add(id);
  if (store.state.justTicked.size === 0) return;
  for (const id of store.state.justTicked.keys()) store.folded.add(id);
  store.state = { overlay: store.state.overlay, justTicked: EMPTY_JUST_TICKED };
  for (const listener of store.listeners) listener();
}

/**
 * What every surface with a live reader currently renders open, as
 * `{ surfaceKey: ids }`. The tick path reads it (see `catchUpReaders`).
 */
function openIdsPerReader(): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const [key, store] of stores) {
    if (store.listeners.size === 0) continue;
    const ids = store.lastRendered.filter((r) => !r.closed).map((r) => r.id);
    if (ids.length > 0) out.set(key, ids);
  }
  return out;
}

/**
 * The device's read of a set of records (docs/SYNC.md §7.2), through the route
 * handler at `/api/records` rather than a server action: actions are
 * SERIALIZED by the router, so a background read on that queue delays the
 * person's next act and the navigation that follows it. A refused or
 * unreachable call throws; every caller treats that as "not now".
 */
async function fetchRecords(ids: string[]): Promise<LiveRecord[]> {
  const res = await fetch('/api/records', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`records: ${res.status}`);
  return (await res.json()) as LiveRecord[];
}

/**
 * The tick's catch-up for the surfaces that read the store (docs/SYNC.md §7.3,
 * the just-ticked rule). A tick is the server saying "something you render moved"
 * without saying what, and the refresh it triggers reads the surface again - where
 * a line someone just closed is simply GONE, because closed work is hidden.
 * That is the one thing the just-ticked rule exists to prevent: a shopper must
 * still see "this is done", struck in its seat, until they leave.
 *
 * So before the refresh lands, the device re-reads the records of what each
 * reader surface renders OPEN and folds them in through `pushToStore`, which
 * snapshots any that came back closed exactly as a push would. The refresh then
 * hides the row and `withJustTicked` stands it back in, struck. A failure is
 * harmless: the refresh still runs, and the surface is merely as it was before
 * slice 3.
 */
export async function catchUpReaders(): Promise<void> {
  const per = openIdsPerReader();
  if (per.size === 0) return;
  const ids = [...new Set([...per.values()].flat())];
  if (ids.length === 0) return;
  let fetched: LiveRecord[];
  try {
    fetched = await fetchRecords(ids);
  } catch {
    return;
  }
  const byId = new Map(fetched.map((r) => [r.id, r]));
  for (const [key, surfaceIds] of per) {
    const rows = surfaceIds
      .map((id) => byId.get(id))
      .filter((r): r is LiveRecord => r !== undefined);
    if (rows.length > 0) pushToStore(key, rows, []);
  }
}

/** Record what a surface last rendered, so a later push can tell an id is on
 *  screen open. Exported for the store's unit test. */
export function setLastRendered(surfaceKey: string, rows: ListRow[]): void {
  const store = stores.get(surfaceKey);
  if (!store) return;
  store.lastRendered = rows;
  if (store.folded.size === 0) return;
  const open = new Set(rows.filter((r) => !r.closed).map((r) => r.id));
  for (const id of store.folded) if (!open.has(id)) store.folded.delete(id);
}

export interface LiveRows {
  /** The server rows patched by pushes, with the just-ticked rule's struck rows
   *  stood back in where the current rows lack them. */
  rows: ListRow[];
  /** The ids the server rows and the pushes carry, WITHOUT the rows the
   *  just-ticked rule stood back in. The server's hidden-closed count is relative
   *  to this set: a row the just-ticked rule stands back in was hidden by the
   *  server and counted there, so a footer that counts hidden work must know
   *  it is standing in plain view (`hiddenClosedCount`). */
  servedIds: ReadonlySet<string>;
  /** The just-ticked rule's rows, for the surface to union into `useCompletion` so a
   *  remote closure renders exactly like a local tick. */
  remoteJustTicked: ReadonlyMap<string, JustTickedRow>;
}

/**
 * The server rows patched by this surface's live pushes and just-ticked rule. Pass
 * `active=false` when the caller is NOT consuming the store this render (a lens
 * on its board/map/checklist rail): the hook then registers no subscriber and
 * returns the props untouched, so `pushToStore` reports no reader and the
 * connection falls back to a refresh for that surface. `scope` is the same
 * string the surface's `useCompletion` uses (e.g. `${JSON.stringify(criteria)}|${op}`,
 * or the list id): the just-ticked rule is cleared when it changes.
 *
 * The overlay is cleared whenever `propRows` changes (a refresh); the sitting
 * rule is cleared only on a scope change or the last reader leaving. Both run in
 * effects, never during render: the lint rules (and React) forbid touching a
 * store mid-render, and after a refresh the fresh rows already reflect the
 * pushes, so nothing stale shows in the frame before the effect runs.
 */
export function useLiveRows(
  surfaceKey: string,
  propRows: ListRow[],
  scope: string,
  active = true,
  /** The surface's filter, for the LOCAL first paint (docs/SYNC.md §7.2): with
   *  no server rows yet, the device evaluates it over the records it holds for
   *  this surface's remembered membership. Omitted = no local paint. */
  local?: { criteria: Criterion[]; op: CriteriaOp; showClosed: boolean },
  /** Rows the surface read beside the props (the rest of a group of a lens,
   *  docs/LENSES.md §4a): served like the props, the overlay applied, a prop
   *  row winning for the same id. A change of these alone keeps the overlay,
   *  since they are no fresher server truth than the props it was cleared
   *  for. */
  extra: readonly ListRow[] = NO_ROWS,
): LiveRows {
  const sub = useCallback(
    (listener: () => void) => (active ? subscribe(surfaceKey, listener) : () => {}),
    [surfaceKey, active],
  );
  const snap = useCallback(
    () => (active ? getSnapshot(surfaceKey) : EMPTY_STATE),
    [surfaceKey, active],
  );
  const state = useSyncExternalStore(sub, snap, snap);
  const held = useHeldRecords();
  useEffect(() => {
    if (active) setSurfaceScope(surfaceKey, scope);
  }, [surfaceKey, scope, active]);
  useEffect(() => {
    if (active) resetSurface(surfaceKey);
  }, [surfaceKey, propRows, active]);
  // The local first paint stands in only while the server has sent nothing;
  // `localFirstPaint` is the identity as soon as props carry rows.
  const base = useMemo(
    () =>
      local && active
        ? localFirstPaint(
            propRows,
            held,
            surfaceMembership(surfaceKey),
            local.criteria,
            local.op,
            local.showClosed,
          )
        : propRows,
    // The local paint is a first-frame stand-in, re-derived when the props or
    // the filter move; a record arriving later reaches the rows through the
    // overlay, so `held` is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [propRows, surfaceKey, local?.criteria, local?.op, local?.showClosed],
  );
  const withExtra = useMemo(() => {
    if (extra.length === 0) return base;
    const ids = new Set(base.map((r) => r.id));
    return [...base, ...extra.filter((r) => !ids.has(r.id))];
  }, [base, extra]);
  const served = useMemo(() => applyOverlay(withExtra, state.overlay), [withExtra, state.overlay]);
  const servedIds = useMemo(() => new Set(served.map((r) => r.id)), [served]);
  const rows = useMemo(() => withJustTicked(served, state.justTicked), [served, state.justTicked]);
  useEffect(() => {
    if (!active) return;
    setLastRendered(surfaceKey, rows);
    // The MEMBERSHIP is what the props seed (docs/SYNC.md §7.2): which ids this
    // surface last rendered, so a cold start can paint it locally. The RECORDS
    // themselves cannot come from here - a `ListRow` is a projection of a
    // record, never one - so the idle pass fetches them (`useIdlePrefetch`).
    setSurfaceMembership(
      surfaceKey,
      rows.map((r) => r.id),
    );
  }, [surfaceKey, rows, active]);
  useIdlePrefetch(rows, active);
  return { rows, servedIds, remoteJustTicked: state.justTicked };
}

// --- the device store (docs/SYNC.md §7.2, slice 3) -------------------------

/**
 * The record shape, the list projection and the board projection are the READ
 * LAYER's (`src/read/record-shape.ts`, docs/SYNC.md §7.1): a pure, I/O-free
 * module both halves import, so what the server pushes and what the device
 * holds is ONE shape with ONE set of projections. The device stores
 * `LiveRecord` (the record with its two dates as epoch millis), because
 * IndexedDB and the wire have no `Date`.
 */

/**
 * Merge one record into what the device holds by the freshness rule the whole
 * engine uses (SYNC.md §7.2): the greater `updatedAt` wins, and on a tie the
 * arriving one does (it is at least as new). Pure.
 */
export function mergeRecord(held: LiveRecord | undefined, arriving: LiveRecord): LiveRecord {
  if (!held) return arriving;
  return held.updatedAt > arriving.updatedAt ? held : arriving;
}

/** The LRU bound on the device's records (docs/SYNC.md §7.2: "bounded by
 *  footprints and LRU"). Records are small; a couple of thousand covers every
 *  surface a phone visits in a session. */
export const RECORD_LIMIT = 2000;

/**
 * What an LRU of `limit` keeps and evicts from a touch order (oldest first, the
 * tail newest). Pure.
 */
export function boundOrder(
  order: readonly string[],
  limit: number,
): { kept: string[]; evicted: string[] } {
  if (order.length <= limit) return { kept: [...order], evicted: [] };
  const cut = order.length - limit;
  return { kept: order.slice(cut), evicted: order.slice(0, cut) };
}

interface DeviceState {
  records: ReadonlyMap<string, LiveRecord>;
  /** Bumped on every change, so `useSyncExternalStore` has a cheap snapshot. */
  version: number;
}

const device: {
  state: DeviceState;
  /** Touch order, oldest first; the LRU evicts from the head. */
  order: string[];
  membership: Map<string, string[]>;
  cursors: Map<string, string>;
  listeners: Set<() => void>;
  hydrated: boolean;
} = {
  state: { records: new Map(), version: 0 },
  order: [],
  membership: new Map(),
  cursors: new Map(),
  listeners: new Set(),
  hydrated: false,
};

function wakeDevice(): void {
  for (const listener of device.listeners) listener();
}

function subscribeDevice(listener: () => void): () => void {
  device.listeners.add(listener);
  return () => {
    device.listeners.delete(listener);
  };
}

function deviceSnapshot(): DeviceState {
  return device.state;
}

/** The record the device holds for an id, or undefined. */
export function heldRecord(id: string): LiveRecord | undefined {
  return device.state.records.get(id);
}

/** Every record the device holds (the local lens evaluation's input). */
export function heldRecords(): ReadonlyMap<string, LiveRecord> {
  return device.state.records;
}

/** The ids a surface last rendered, as the device remembers them. */
export function surfaceMembership(key: string): readonly string[] {
  return device.membership.get(key) ?? [];
}

/** The last cursor the server sent for a surface, or null (docs/SYNC.md §5.6:
 *  handed back on a RE-connect so the server replays the gap instead of the
 *  tab refreshing blind). Opaque: never parsed. */
export function surfaceCursor(key: string): string | null {
  return device.cursors.get(key) ?? null;
}

/** Record a surface's cursor. Persisted with its membership. */
export function setSurfaceCursor(key: string, cur: string): void {
  if (device.cursors.get(key) === cur) return;
  device.cursors.set(key, cur);
  void persistSurface({ key, ids: [...surfaceMembership(key)], cur });
}

/** Record what a surface rendered, so a cold start can paint it locally. */
export function setSurfaceMembership(key: string, ids: string[]): void {
  const held = device.membership.get(key);
  if (held && held.length === ids.length && held.every((id, i) => id === ids[i])) return;
  device.membership.set(key, ids);
  void persistSurface({ key, ids, cur: surfaceCursor(key) });
}

/**
 * Fold records into the device store: merged by `updatedAt`, touched for the
 * LRU, persisted, and announced to every reader. A no-op for an empty list.
 */
export function putRecords(records: LiveRecord[]): void {
  if (records.length === 0) return;
  const arriving = new Set(records.map((r) => r.id));
  const next = new Map(device.state.records);
  const order = device.order.filter((id) => !arriving.has(id));
  const touched = Date.now();
  const puts: StoredRecord[] = [];
  for (const record of records) {
    const merged = mergeRecord(next.get(record.id), record);
    next.set(record.id, merged);
    order.push(record.id);
    puts.push({ id: record.id, record: merged, touched });
  }
  const { kept, evicted } = boundOrder(order, RECORD_LIMIT);
  for (const id of evicted) next.delete(id);
  device.order = kept;
  device.state = { records: next, version: device.state.version + 1 };
  void persistRecords(puts, evicted);
  wakeDevice();
}

/** Drop everything the device holds (a different person, or a new build).
 *  Exported for the store's unit test. */
export function clearDevice(): void {
  device.state = { records: new Map(), version: device.state.version + 1 };
  device.order = [];
  device.membership.clear();
  device.cursors.clear();
  device.hydrated = false;
  wakeDevice();
}

/**
 * Adopt the device store for `<viewerRef>|<buildId>`: the persisted records,
 * memberships and cursors come back when the identity matches, and are dropped
 * when it does not - another person on this phone (SYNC.md §7.4: the store is
 * keyed by person id), or a new build (the record shapes are the build's, the
 * same invalidation the build watchdog makes for a stale tab). Runs once per
 * page load.
 */
export async function hydrateDevice(identity: string): Promise<void> {
  if (device.hydrated) return;
  device.hydrated = true;
  const kept = await adoptIdentity(identity);
  if (!kept) return;
  const [records, surfaces] = await Promise.all([loadRecords(), loadSurfaces()]);
  for (const s of surfaces) {
    device.membership.set(s.key, s.ids);
    if (s.cur) device.cursors.set(s.key, s.cur);
  }
  const map = new Map(device.state.records);
  const order: string[] = [];
  // `loadRecords` is newest touch first; the order array is oldest first.
  for (const stored of [...records].reverse()) {
    const record = stored.record as LiveRecord | undefined;
    if (!record || typeof record.id !== 'string') continue;
    if (map.has(record.id)) continue; // a live push already landed a fresher one
    map.set(record.id, record);
    order.push(record.id);
  }
  device.order = [...order, ...device.order];
  device.state = { records: map, version: device.state.version + 1 };
  wakeDevice();
}

/** The record the device holds for an id, re-rendering when it changes. */
export function useRecord(id: string | null): LiveRecord | undefined {
  const snap = useSyncExternalStore(subscribeDevice, deviceSnapshot, deviceSnapshot);
  return id ? snap.records.get(id) : undefined;
}

/** Every record the device holds, re-rendering when any changes. */
export function useHeldRecords(): ReadonlyMap<string, LiveRecord> {
  return useSyncExternalStore(subscribeDevice, deviceSnapshot, deviceSnapshot).records;
}

/**
 * The surface's LOCAL first paint (docs/SYNC.md §7.2): the rows the device can
 * already show, evaluated here - `criteriaMatch` is pure - over the records it
 * holds for the ids that surface last rendered, under the surface's criteria and
 * show-closed flag. The server's rows then win outright: with props present this is
 * the identity, because the server's set IS the membership and a local set could
 * only be older. Pure.
 */
export function localFirstPaint(
  propRows: ListRow[],
  records: ReadonlyMap<string, LiveRecord>,
  memberIds: readonly string[],
  criteria: Criterion[],
  op: CriteriaOp,
  showClosed: boolean,
): ListRow[] {
  if (propRows.length > 0 || memberIds.length === 0 || records.size === 0) return propRows;
  const closedShown = effectiveShowClosed(criteria, showClosed);
  const ancestors = criteria.some((c) => c.field === 'ancestor')
    ? heldAncestors(memberIds, records)
    : new Map<string, Set<string>>();
  const out: ListRow[] = [];
  for (const id of memberIds) {
    const record = records.get(id);
    if (!record) continue;
    if (!passesShowClosed(record, closedShown)) continue;
    const node = {
      title: record.title,
      // A record held from before the field existed has none, so it matches
      // no party filter until refreshed.
      requesters: record.requesters ?? [],
      contributors: record.contributors ?? [],
      tags: record.tags ?? [],
      // The record's effective groups (a record held from before the field
      // existed has none, so it matches no board filter until refreshed).
      groupIds: record.groupIds ?? [],
      // The record's VISIBLE parents (a record held from before the field
      // existed has none, so it matches no parent filter until refreshed).
      parentIds: (record.parents ?? []).map((p) => p.id),
      // The record's own ancestors, the server's walk (a record held from
      // before the field existed has none, and the held records' walk stands in).
      ancestorIds: record.ancestorIds ?? [...(ancestors.get(id) ?? [])],
      // Same for the user tags (a record from before the field has none).
      itemTags: record.itemTags ?? [],
      delivered: record.delivered,
      active: record.active,
      closed: record.closed,
      verdict: record.verdict,
    };
    if (!criteriaMatch(node, criteria, op)) continue;
    out.push(liveRecordToListRow(record));
  }
  return out;
}

/**
 * Every ancestor the device can name for each id: the upward walk over the
 * held records' VISIBLE parents (`ancestorSetsOf`). A parent the device holds
 * no record of still counts, and its own parents are unknown here, so a first
 * paint may keep a row the server's set leaves out; the server's rows win.
 */
function heldAncestors(
  ids: readonly string[],
  records: ReadonlyMap<string, LiveRecord>,
): Map<string, Set<string>> {
  const edges: ParentEdge[] = [];
  const known = new Set(records.keys());
  for (const [childId, record] of records) {
    for (const p of record.parents ?? []) {
      edges.push({ childId, parentId: p.id });
      known.add(p.id);
    }
  }
  return ancestorSetsOf(ids, edges, known);
}

/**
 * A board's cards re-projected from the records the device holds (docs/SYNC.md
 * §7.2: "lists, boards, checklists and the item page become client-side
 * projections of records"). The card the server rendered supplies the BOARD's
 * own reading of the item (`BoardCardContext`: where it sits, this board's
 * digest and signals, plus the two `WorkItemState` fields a record does not
 * carry - the full owner set and `inheritsAccess`); the record supplies
 * everything that is the item's own, through `recordToBoardItem` - the same
 * projection the server would use.
 *
 * A card carries no `updatedAt` of its own - `WorkItemState` has none - so the
 * reference point is WHEN THE PROPS ARRIVED: a record written after this render
 * wins, anything older loses to the server's rows. That is the same law the row
 * overlay applies, with the only clock the card has.
 */
export function useCardsFromRecords<T extends RecordBoardItem>(cards: T[]): T[] {
  const records = useHeldRecords();
  // The arrival is stamped in an EFFECT (reading a clock during render is
  // impure) into a WeakMap keyed by the props themselves, so no state change
  // and no cascading render: the next store change - which is the only thing
  // that can make a record newer - re-reads it. Unstamped means "the props are
  // this frame's truth", which is what a first render is.
  useEffect(() => {
    cardsArrivedAt.set(cards, Date.now());
  }, [cards]);
  return useMemo(() => {
    const at = cardsArrivedAt.get(cards) ?? Number.POSITIVE_INFINITY;
    if (records.size === 0) return cards;
    let changed = false;
    const out = cards.map((card) => {
      const record = records.get(card.id);
      if (!record || record.updatedAt <= at) return card;
      changed = true;
      return {
        ...card,
        ...recordToBoardItem(fromLiveRecord(record), {
          // The record flattens the owner set to its primary, so a card whose
          // record names none keeps the seats the server resolved.
          requesters: record.requester ? [record.requester] : card.state.requesters,
          inheritsAccess: card.state.inheritsAccess,
          children: card.children,
          pulled: card.pulled,
          digestCount: card.digestCount,
          rank: card.rank,
          laneId: card.laneId,
          pinned: card.pinned,
        }),
      };
    });
    return changed ? out : cards;
  }, [cards, records]);
}

/** When a board's cards last arrived from the server, keyed by the props array. */
const cardsArrivedAt = new WeakMap<object, number>();

// --- the idle prefetch one hop out (docs/SYNC.md §7.2) ---------------------

/** How many neighbour records one idle pass asks for. A hop out of a screenful
 *  of rows, never the graph. */
const PREFETCH_LIMIT = 60;

function runWhenIdle(cb: () => void): void {
  if (typeof window === 'undefined') return;
  const ric = (window as unknown as { requestIdleCallback?: (c: () => void) => void })
    .requestIdleCallback;
  if (ric) ric(cb);
  else setTimeout(cb, 500);
}

/**
 * The ids this surface needs records for and the device does NOT hold: the
 * rows ON SCREEN first (a server-rendered row is a PROJECTION of a record, not
 * a record, so the device cannot hold one until it reads it), then one hop out
 * - a row's parent, and the neighbours a held record names. Capped. Pure.
 */
export function recordIdsToFetch(
  rows: ListRow[],
  records: ReadonlyMap<string, LiveRecord>,
  limit = PREFETCH_LIMIT,
): string[] {
  const want = new Set<string>();
  const consider = (id: string | undefined) => {
    if (!id || records.has(id) || want.size >= limit) return;
    want.add(id);
  };
  for (const row of rows) consider(row.id);
  for (const row of rows) {
    consider(row.parent?.id);
    const n = records.get(row.id)?.neighbours;
    if (!n) continue;
    for (const brief of [...n.parents, ...n.children, ...n.blockers, ...n.blocking, ...n.relates]) {
      consider(brief.id);
    }
  }
  return [...want];
}

/**
 * How long a surface must sit still before the prefetch goes out. Server
 * actions are SERIALIZED by the router, so a prefetch in flight delays the
 * person's next act and its refresh; the settle window keeps the queue free
 * while anyone is typing, ticking or dragging.
 */
const PREFETCH_SETTLE_MS = 2000;

/**
 * After a surface settles, fetch the records it rendered and the ones one hop
 * out, in idle time (docs/SYNC.md §7.2), so the surface paints from the device
 * on the next cold open and an opened neighbour is instant and reads offline.
 * Skipped while offline (nothing to gain, and the action would throw) and
 * whenever the device already holds every neighbour.
 */
export function useIdlePrefetch(rows: ListRow[], active = true): void {
  // The rows as they stand when the pass fires, WITHOUT re-arming it: one pass
  // per mounted surface. Re-arming on every row change meant a wide read going
  // out after every quiet moment, competing with the person's own writes on the
  // router's serialized action queue.
  const latest = useRef(rows);
  useEffect(() => {
    latest.current = rows;
  }, [rows]);
  useEffect(() => {
    if (!active) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    let cancelled = false;
    const fetchHop = () => {
      if (cancelled || latest.current.length === 0) return;
      // Read the held records HERE, not as a dependency: a pass that stores
      // records would otherwise schedule the next pass, forever.
      const ids = recordIdsToFetch(latest.current, heldRecords());
      if (ids.length === 0) return;
      void fetchRecords(ids)
        .then((fetched) => {
          if (!cancelled) putRecords(fetched);
        })
        .catch(() => {
          // Offline or refused: the prefetch is an optimization, never a path.
        });
    };
    const settle = setTimeout(() => {
      if (!cancelled) runWhenIdle(fetchHop);
    }, PREFETCH_SETTLE_MS);
    return () => {
      cancelled = true;
      clearTimeout(settle);
    };
  }, [active]);
}
